import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Lock, Envelope, WarningCircle, ShieldCheck, ArrowLeft, PaperPlaneTilt, CheckCircle, Fingerprint } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_566bb76b-f932-4e23-952a-9935b75c7780/artifacts/i6y9m6q5_53561592_transparent11%20-%20Copy.png";

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForceChange, setShowForceChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const { login, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();

  // 2FA OTP
  const [showOtp, setShowOtp] = useState(false);
  const [otpUserId, setOtpUserId] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpName, setOtpName] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [isNewDevice, setIsNewDevice] = useState(false);
  const [otpMustChange, setOtpMustChange] = useState(false);
  const [resending, setResending] = useState(false);
  const [hasBiometric, setHasBiometric] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [showRegisterBiometric, setShowRegisterBiometric] = useState(false);
  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  // Forgot Password
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState('');

  // Session expired check
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('expired') === '1') {
      setError('Session expired. Please log in again.');
    }
  }, []);

  // WebAuthn browser support check
  const supportsWebAuthn = typeof window !== 'undefined' && window.PublicKeyCredential;

  const handleBiometricAuth = async () => {
    setBiometricLoading(true);
    setError('');
    try {
      // Get authentication options from server
      const optRes = await authApi.webauthnAuthOptions(otpUserId);
      const options = optRes.data;

      // Convert base64url challenge to ArrayBuffer
      const challenge = Uint8Array.from(atob(options.challenge.replace(/-/g,'+').replace(/_/g,'/')), c => c.charCodeAt(0));
      const allowCredentials = (options.allowCredentials || []).map(c => ({
        ...c, id: Uint8Array.from(atob(c.id.replace(/-/g,'+').replace(/_/g,'/')), ch => ch.charCodeAt(0)),
      }));

      // Trigger browser biometric prompt
      const credential = await navigator.credentials.get({
        publicKey: { ...options, challenge, allowCredentials, timeout: 60000 },
      });

      // Convert response to JSON-safe format
      const credJson = {
        id: credential.id,
        rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,''),
        type: credential.type,
        response: {
          authenticatorData: btoa(String.fromCharCode(...new Uint8Array(credential.response.authenticatorData))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,''),
          clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(credential.response.clientDataJSON))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,''),
          signature: btoa(String.fromCharCode(...new Uint8Array(credential.response.signature))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,''),
        },
        authenticatorAttachment: credential.authenticatorAttachment || null,
      };

      // Verify with server
      const verifyRes = await authApi.webauthnAuthVerify(otpUserId, credJson);
      if (verifyRes.data?.access_token) {
        localStorage.setItem('access_token', verifyRes.data.access_token);
        if (verifyRes.data.must_change_password) {
          setShowForceChange(true); setShowOtp(false);
        } else {
          const role = verifyRes.data.role;
          if (role === 'super_admin' || ['platform_admin','support_team','account_team','technical_team'].includes(role)) {
            navigate('/admin-dashboard');
          } else navigate('/dashboard');
        }
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') setError('Biometric verification cancelled');
      else setError(err.response?.data?.detail || 'Biometric verification failed');
    } finally { setBiometricLoading(false); }
  };

  const handleRegisterBiometric = async () => {
    setBiometricLoading(true);
    setError('');
    try {
      const optRes = await authApi.webauthnRegisterOptions(otpUserId);
      const options = optRes.data;

      const challenge = Uint8Array.from(atob(options.challenge.replace(/-/g,'+').replace(/_/g,'/')), c => c.charCodeAt(0));
      const userId = Uint8Array.from(atob(options.user.id.replace(/-/g,'+').replace(/_/g,'/')), c => c.charCodeAt(0));
      const excludeCredentials = (options.excludeCredentials || []).map(c => ({
        ...c, id: Uint8Array.from(atob(c.id.replace(/-/g,'+').replace(/_/g,'/')), ch => ch.charCodeAt(0)),
      }));

      const credential = await navigator.credentials.create({
        publicKey: { ...options, challenge, user: { ...options.user, id: userId }, excludeCredentials, timeout: 60000 },
      });

      const credJson = {
        id: credential.id,
        rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,''),
        type: credential.type,
        response: {
          attestationObject: btoa(String.fromCharCode(...new Uint8Array(credential.response.attestationObject))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,''),
          clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(credential.response.clientDataJSON))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,''),
        },
        authenticatorAttachment: credential.authenticatorAttachment || null,
      };

      await authApi.webauthnRegisterVerify(otpUserId, credJson, 'Biometric Device');
      setHasBiometric(true);
      setShowRegisterBiometric(false);
      setError('');
      alert('Biometric registered! You can now use fingerprint/face to verify on next login.');
    } catch (err) {
      if (err.name === 'NotAllowedError') setError('Biometric registration cancelled');
      else setError(err.response?.data?.detail || 'Registration failed');
    } finally { setBiometricLoading(false); }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const result = await login(email, password);
    
    if (result.requires_otp) {
      // 2FA required
      setOtpUserId(result.user_id);
      setOtpEmail(result.email);
      setOtpName(result.name);
      setIsNewDevice(result.is_new_device);
      setOtpMustChange(result.must_change_password);
      setHasBiometric(result.has_biometric || false);
      setShowOtp(true);
      setLoading(false);
      setTimeout(() => otpRefs[0]?.current?.focus(), 100);
      return;
    }
    
    if (result.success) {
      if (result.must_change_password) {
        setShowForceChange(true);
        setLoading(false);
        return;
      }
      if (result.user?.role === 'super_admin') navigate('/admin-dashboard');
      else navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otpCode];
    newOtp[index] = value.slice(-1);
    setOtpCode(newOtp);
    
    // Auto-focus next
    if (value && index < 5) otpRefs[index + 1]?.current?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpRefs[index - 1]?.current?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtpCode(pasted.split(''));
      otpRefs[5]?.current?.focus();
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const code = otpCode.join('');
    if (code.length !== 6) { setError('Please enter the 6-digit code'); return; }
    
    setOtpLoading(true);
    setError('');
    
    const result = await verifyOtp(otpUserId, code);
    
    if (result.success) {
      if (result.must_change_password || otpMustChange) {
        setShowOtp(false);
        setShowForceChange(true);
        setOtpLoading(false);
        return;
      }
      if (result.user?.role === 'super_admin') navigate('/admin-dashboard');
      else navigate('/dashboard');
    } else {
      setError(result.error);
      setOtpCode(['', '', '', '', '', '']);
      otpRefs[0]?.current?.focus();
    }
    setOtpLoading(false);
  };

  const handleResendOtp = async () => {
    setResending(true);
    await resendOtp(otpUserId);
    setResending(false);
    setError('');
    setOtpCode(['', '', '', '', '', '']);
    otpRefs[0]?.current?.focus();
  };

  const handleForceChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    setChangingPassword(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      const res = await authApi.forceChangePassword(newPassword, token);
      if (res.data?.message) {
        const role = res.data?.role || '';
        if (role === 'super_admin') navigate('/admin-dashboard');
        else navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { setForgotError('Please enter your email'); return; }
    setForgotLoading(true);
    setForgotError('');
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotSuccess(true);
    } catch (err) {
      setForgotError(err.response?.data?.detail || 'Failed to send reset email. Please contact admin@stewardindia.com');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side */}
      <div className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/12081340/pexels-photo-12081340.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-sky-900/70 to-violet-900/80"></div>
        <div className="relative z-10 flex flex-col justify-center items-center text-white p-12">
          <img src={LOGO_URL} alt="Care'O Logo" className="h-24 mb-8 brightness-0 invert" />
          <h1 className="text-4xl font-light mb-4 font-['Outfit']">Hospital Claim Management</h1>
          <p className="text-lg opacity-90 text-center max-w-md text-sky-100">
            Streamline your revenue cycle with intelligent claim processing and real-time analytics.
          </p>
          <div className="mt-8 flex gap-4">
            <div className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-sm text-sky-200 border border-white/20">Multi-Tenant</div>
            <div className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-sm text-sky-200 border border-white/20">HIPAA Compliant</div>
            <div className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-sm text-sky-200 border border-white/20">2FA Secured</div>
          </div>
          <p className="mt-6 text-xs text-sky-300/60">Ver 0.1</p>
        </div>
      </div>

      {/* Right side */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">

        {/* ========== FORGOT PASSWORD ========== */}
        {showForgot ? (
          <Card className="w-full max-w-md border-slate-200 shadow-xl shadow-slate-200/50">
            <CardHeader className="text-center">
              {forgotSuccess ? (
                <>
                  <div className="h-14 w-14 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                    <CheckCircle className="h-7 w-7 text-emerald-600" weight="duotone" />
                  </div>
                  <CardTitle className="text-xl font-['Outfit'] text-slate-800">Password Sent!</CardTitle>
                  <CardDescription className="text-slate-500 mt-2">
                    A new password has been sent to <span className="font-semibold text-slate-700">{forgotEmail}</span>.
                  </CardDescription>
                </>
              ) : (
                <>
                  <div className="h-14 w-14 mx-auto bg-sky-100 rounded-full flex items-center justify-center mb-3">
                    <Lock className="h-7 w-7 text-sky-600" weight="duotone" />
                  </div>
                  <CardTitle className="text-xl font-['Outfit'] text-slate-800">Forgot Password?</CardTitle>
                  <CardDescription className="text-slate-500 mt-1">Enter your registered email. A new password will be sent to you.</CardDescription>
                </>
              )}
            </CardHeader>
            <CardContent>
              {forgotSuccess ? (
                <div className="space-y-4">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-emerald-700">You will be asked to set a new password on your first login.</p>
                  </div>
                  <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white"
                    onClick={() => { setShowForgot(false); setForgotSuccess(false); setForgotEmail(''); }}>
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back to Login
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  {forgotError && <Alert variant="destructive" className="bg-red-50 border-red-200 text-red-700"><WarningCircle className="h-4 w-4" /><AlertDescription>{forgotError}</AlertDescription></Alert>}
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <div className="relative">
                      <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input type="email" placeholder="Enter your registered email" value={forgotEmail}
                        onChange={e => setForgotEmail(e.target.value)} className="pl-10 border-slate-300" required data-testid="forgot-email-input" />
                    </div>
                  </div>
                  <Button type="submit" disabled={forgotLoading} className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white" data-testid="forgot-submit-btn">
                    {forgotLoading ? 'Sending...' : <><PaperPlaneTilt className="h-4 w-4 mr-2" /> Send New Password</>}
                  </Button>
                  <button type="button" onClick={() => { setShowForgot(false); setForgotError(''); }}
                    className="w-full text-sm text-slate-500 hover:text-sky-600 flex items-center justify-center gap-1 mt-2">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to Login
                  </button>
                </form>
              )}
            </CardContent>
          </Card>

        ) : showOtp ? (
          /* ========== 2FA VERIFICATION ========== */
          <Card className="w-full max-w-md border-sky-200 shadow-xl shadow-sky-200/30" data-testid="otp-card">
            <CardHeader className="text-center">
              <div className="h-14 w-14 mx-auto bg-sky-100 rounded-full flex items-center justify-center mb-3">
                <Fingerprint className="h-7 w-7 text-sky-600" weight="duotone" />
              </div>
              <CardTitle className="text-xl font-['Outfit'] text-slate-800">Two-Factor Verification</CardTitle>
              <CardDescription className="text-slate-500 mt-1">
                A 6-digit code has been sent to <span className="font-semibold text-sky-700">{otpEmail}</span>
              </CardDescription>
              {isNewDevice && (
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <p className="text-[10px] text-amber-700 font-semibold flex items-center gap-1 justify-center">
                    <WarningCircle className="h-3.5 w-3.5" /> New device/location detected
                  </p>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {/* Biometric Auth Button (when already registered) */}
              {hasBiometric && supportsWebAuthn && (
                <div className="mb-5">
                  <Button type="button" onClick={handleBiometricAuth} disabled={biometricLoading}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/25 h-12 text-sm gap-2"
                    data-testid="biometric-auth-btn">
                    <Fingerprint className="h-5 w-5" weight="bold" />
                    {biometricLoading ? 'Verifying Biometric...' : 'Verify with Fingerprint / Face ID'}
                  </Button>
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">or use OTP</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                </div>
              )}

              {/* Register Biometric Prompt (when NOT yet registered) */}
              {supportsWebAuthn && !hasBiometric && (
                <div className="mb-5 p-3 rounded-xl bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200" data-testid="biometric-setup-card">
                  {!showRegisterBiometric ? (
                    <button type="button" onClick={() => setShowRegisterBiometric(true)}
                      className="w-full flex items-center justify-center gap-2 text-sm text-teal-700 hover:text-teal-800 font-semibold py-1"
                      data-testid="setup-biometric-btn">
                      <Fingerprint className="h-5 w-5 text-teal-600" weight="duotone" /> Setup Fingerprint / Face ID for faster login
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-[10px] text-teal-700 text-center font-medium">Register your biometric for instant verification on this device</p>
                      <Button type="button" onClick={handleRegisterBiometric} disabled={biometricLoading}
                        className="w-full bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white text-xs h-10 gap-1.5 shadow-md"
                        data-testid="register-biometric-btn">
                        <Fingerprint className="h-4 w-4" weight="bold" />
                        {biometricLoading ? 'Registering...' : 'Register Fingerprint / Face ID Now'}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <form onSubmit={handleOtpSubmit} className="space-y-5">
                {error && <Alert variant="destructive" className="bg-red-50 border-red-200 text-red-700"><WarningCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
                
                <div className="flex justify-center gap-2" onPaste={handleOtpPaste} data-testid="otp-inputs">
                  {otpCode.map((digit, i) => (
                    <input key={i} ref={otpRefs[i]} type="text" inputMode="numeric" maxLength={1}
                      value={digit} onChange={e => handleOtpChange(i, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(i, e)}
                      className="w-12 h-14 text-center text-2xl font-bold font-mono border-2 border-slate-300 rounded-xl focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition-colors text-slate-800"
                      data-testid={`otp-digit-${i}`} />
                  ))}
                </div>

                <p className="text-center text-xs text-slate-400">Code expires in 5 minutes</p>

                <Button type="submit" disabled={otpLoading || otpCode.join('').length !== 6}
                  className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white shadow-lg shadow-sky-500/25"
                  data-testid="verify-otp-btn">
                  {otpLoading ? 'Verifying...' : 'Verify & Login'}
                </Button>

                <div className="flex items-center justify-between">
                  <button type="button" onClick={() => { setShowOtp(false); setError(''); setOtpCode(['','','','','','']); }}
                    className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
                    <ArrowLeft className="h-3 w-3" /> Back
                  </button>
                  <button type="button" onClick={handleResendOtp} disabled={resending}
                    className="text-xs text-sky-600 hover:text-sky-700 font-medium" data-testid="resend-otp-btn">
                    {resending ? 'Sending...' : 'Resend Code'}
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>

        ) : !showForceChange ? (
          /* ========== LOGIN CARD ========== */
          <Card className="w-full max-w-md border-slate-200 shadow-xl shadow-slate-200/50">
            <CardHeader className="text-center">
              <img src={LOGO_URL} alt="Care'O Logo" className="h-16 mx-auto mb-4 lg:hidden" />
              <CardTitle className="text-2xl font-['Outfit'] text-slate-800">Welcome Back</CardTitle>
              <CardDescription className="text-slate-500">Sign in to access your claim management dashboard</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && <Alert variant="destructive" className="bg-red-50 border-red-200 text-red-700"><WarningCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <div className="relative">
                    <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input id="email" type="email" placeholder="Enter your email" value={email}
                      onChange={e => setEmail(e.target.value)} className="pl-10 border-slate-300" required data-testid="login-email-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-700">Password</Label>
                    <button type="button" onClick={() => { setShowForgot(true); setForgotEmail(email); setError(''); }}
                      className="text-xs text-sky-600 hover:text-sky-700 font-medium hover:underline" data-testid="forgot-password-link">
                      Forgot Password?
                    </button>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input id="password" type="password" placeholder="Enter your password" value={password}
                      onChange={e => setPassword(e.target.value)} className="pl-10 border-slate-300" required data-testid="login-password-input" />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white shadow-lg shadow-sky-500/25"
                  disabled={loading} data-testid="login-submit-button">
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
              <div className="mt-5 pt-4 border-t border-slate-100 text-center">
                <p className="text-xs text-slate-400">Having trouble logging in?</p>
                <p className="text-xs text-slate-500 mt-0.5">Please contact Steward Healthcare India Pvt. Ltd. at <a href="mailto:info@stewardindia.com" className="text-sky-600 font-medium hover:underline">info@stewardindia.com</a></p>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* ========== FORCE CHANGE PASSWORD ========== */
          <Card className="w-full max-w-md border-amber-200 shadow-xl shadow-amber-200/30" data-testid="force-change-password-card">
            <CardHeader className="text-center">
              <div className="h-14 w-14 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-3">
                <ShieldCheck className="h-7 w-7 text-amber-600" weight="duotone" />
              </div>
              <CardTitle className="text-xl font-['Outfit'] text-slate-800">Set New Password</CardTitle>
              <CardDescription className="text-slate-500">You must change your temporary password before continuing.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForceChange} className="space-y-4">
                {error && <Alert variant="destructive" className="bg-red-50 border-red-200 text-red-700"><WarningCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
                <div className="space-y-2">
                  <Label className="text-slate-700">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input type="password" placeholder="Min 8 chars, uppercase, lowercase, number, special" value={newPassword}
                      onChange={e => setNewPassword(e.target.value)} className="pl-10 border-amber-300" required data-testid="new-password-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input type="password" placeholder="Re-enter your new password" value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)} className="pl-10 border-amber-300" required data-testid="confirm-password-input" />
                  </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-[10px] text-slate-500 space-y-1">
                  <p className="font-semibold text-slate-600">Password Requirements (HIPAA):</p>
                  <p className={newPassword.length >= 8 ? 'text-emerald-600' : ''}>- Minimum 8 characters</p>
                  <p className={/[A-Z]/.test(newPassword) ? 'text-emerald-600' : ''}>- At least one uppercase letter</p>
                  <p className={/[a-z]/.test(newPassword) ? 'text-emerald-600' : ''}>- At least one lowercase letter</p>
                  <p className={/[0-9]/.test(newPassword) ? 'text-emerald-600' : ''}>- At least one number</p>
                  <p className={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'text-emerald-600' : ''}>- At least one special character</p>
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white"
                  disabled={changingPassword} data-testid="change-password-button">
                  {changingPassword ? 'Changing...' : 'Set New Password & Continue'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
